import React, { useMemo, useState } from "react";
import { Table, Button, Tooltip, Popconfirm, Empty, message } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  RightOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { TeacherTasksAPI } from "../../../../api/teacherTasksAPI";
import { StatusTag, BrandThemeProvider } from "../../../../ui";
import { TaskDetailModal } from "./TaskDetailModal";
import { TaskResultsModal } from "./TaskResultsModal";
import styles from "./TaskListView.module.scss";

/**
 * TaskListView — listado "Mis tareas" (mockup inicio v2-pro).
 *
 * Tabla limpia AntD: Titulo / Clase / Pasos / Estado / Acciones (+ chevron).
 * - Click en la fila (o chevron) -> TaskDetailModal.
 * - Acciones rapidas por fila: Editar (solo draft), Duplicar (deferido al
 *   rediseno del asistente, F3.3), Borrar (reglas del backend).
 *
 * Props:
 *  - accessToken, tasks, loading, onReload
 *  - onEdit(task)   -> abrir edicion (solo draft); opcional
 *  - onNew()        -> abrir creacion; opcional (lo monta el grid)
 */
function getStepsCount(task) {
  if (Array.isArray(task?.steps)) return task.steps.length;
  if (Number.isFinite(task?.stepsCount)) return task.stepsCount;
  return 0;
}

export function TaskListView({
  accessToken,
  tasks = [],
  loading = false,
  onReload,
  onEdit,
}) {
  const { t } = useTranslation("tasks");
  const api = useMemo(() => new TeacherTasksAPI(), []);

  const [detailId, setDetailId] = useState(null);
  const [resultsId, setResultsId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const openDetail = (task) => setDetailId(task._id);

  const handleActivate = async (task) => {
    try {
      setBusyId(task._id);
      await api.activateTask(accessToken, task._id);
      await onReload?.();
    } catch (e) {
      message.error(e?.message || t("list.activateError"));
    } finally {
      setBusyId(null);
    }
  };

  const handleDeactivate = async (task) => {
    try {
      setBusyId(task._id);
      await api.deactivateTask(accessToken, task._id); // backend -> 'archived'
      await onReload?.();
    } catch (e) {
      message.error(e?.message || t("list.archiveError"));
    } finally {
      setBusyId(null);
    }
  };

  const handleReactivate = async (task) => {
    try {
      setBusyId(task._id);
      await api.reactivateTask(accessToken, task._id); // backend -> 'draft'
      message.success(t("list.reactivateSuccess"));
      await onReload?.();
    } catch (e) {
      message.error(e?.message || t("list.reactivateError"));
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (task) => {
    try {
      setBusyId(task._id);
      if (task.status === "active") {
        message.warning(t("list.deleteActiveError"));
        return;
      }
      if (task.status === "archived") {
        await api.purgeArchivedTask(accessToken, task._id);
      } else {
        await api.deleteDraftTask(accessToken, task._id);
      }
      await onReload?.();
    } catch (e) {
      message.error(e?.message || t("list.deleteError"));
    } finally {
      setBusyId(null);
    }
  };

  const stop = (e) => e.stopPropagation();

  const columns = [
    {
      title: t("list.colTitle"),
      dataIndex: "title",
      key: "title",
      render: (title) => <span className={styles.cellTitle}>{title}</span>,
    },
    {
      title: t("list.colClass"),
      key: "class",
      width: 110,
      responsive: ["sm"],
      render: (_, task) => (
        <span className={styles.cellMuted}>
          {task.classnumber || task.classroom?.name || "-"}
        </span>
      ),
    },
    {
      title: t("list.colSteps"),
      key: "steps",
      width: 90,
      align: "center",
      responsive: ["md"],
      render: (_, task) => (
        <span className={styles.cellMuted}>{getStepsCount(task)}</span>
      ),
    },
    {
      title: t("list.colStatus"),
      key: "status",
      width: 130,
      render: (_, task) => <StatusTag status={task.status} />,
    },
    {
      title: t("list.actionsHeader"),
      key: "actions",
      width: 190,
      render: (_, task) => {
        const isDraft = task.status === "draft";
        const isActive = task.status === "active";
        const isArchived = task.status === "archived";
        const canEditTask = isDraft;
        return (
          <div className={styles.actions} onClick={stop}>
            <Tooltip
              title={
                isActive
                  ? t("detail.editOnlyInactive")
                  : isArchived
                    ? t("detail.editOnlyDraft")
                    : t("detail.edit")
              }
            >
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                disabled={!canEditTask || !onEdit}
                onClick={() => onEdit?.(task)}
              />
            </Tooltip>

            {isActive ? (
              <Popconfirm
                title={t("list.deactivateTitle")}
                description={t("list.deactivateNote")}
                okText={t("list.deactivateButton")}
                okButtonProps={{ loading: busyId === task._id }}
                cancelText={t("list.cancelButton")}
                onConfirm={() => handleDeactivate(task)}
              >
                <Tooltip title={t("list.deactivateButton")}>
                  <Button
                    type="text"
                    size="small"
                    icon={<PauseCircleOutlined />}
                    loading={busyId === task._id}
                  />
                </Tooltip>
              </Popconfirm>
            ) : (
              <Tooltip
                title={
                  isArchived
                    ? t("list.reactivateButton")
                    : t("list.activateButton")
                }
              >
                <Button
                  type="text"
                  size="small"
                  icon={<PlayCircleOutlined />}
                  loading={busyId === task._id}
                  onClick={() =>
                    isArchived ? handleReactivate(task) : handleActivate(task)
                  }
                />
              </Tooltip>
            )}

            {isActive ? (
              <Tooltip title={t("list.deleteActiveError")}>
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  disabled
                />
              </Tooltip>
            ) : (
              <Popconfirm
                title={
                  task.status === "archived"
                    ? t("list.deleteArchivedTitle")
                    : t("list.deleteTitle")
                }
                description={t("list.deleteConfirm", { title: task.title })}
                okText={
                  task.status === "archived"
                    ? t("list.deleteButton")
                    : t("list.deleteDraftButton")
                }
                okButtonProps={{ danger: true, loading: busyId === task._id }}
                cancelText={t("list.cancelButton")}
                onConfirm={() => handleDelete(task)}
              >
                <Tooltip title={t("detail.delete")}>
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    loading={busyId === task._id}
                  />
                </Tooltip>
              </Popconfirm>
            )}
          </div>
        );
      },
    },
    {
      key: "chevron",
      width: 36,
      align: "center",
      render: () => <RightOutlined className={styles.chevron} />,
    },
  ];

  return (
    <BrandThemeProvider>
      <div className={styles.wrap}>
        <Table
          className={styles.table}
          rowKey="_id"
          columns={columns}
          dataSource={tasks}
          loading={loading}
          pagination={false}
          scroll={{ x: "max-content" }}
          onRow={(task) => ({
            onClick: () => openDetail(task),
          })}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t("list.noTasks")}
              />
            ),
          }}
          rowClassName={styles.row}
        />

        <div className={styles.hint}>{t("list.rowHint")}</div>

        <TaskDetailModal
          open={!!detailId}
          taskId={detailId}
          accessToken={accessToken}
          onClose={() => setDetailId(null)}
          onChanged={onReload}
          onEdit={(task) => {
            setDetailId(null);
            onEdit?.(task);
          }}
          onOpenResults={(id) => {
            setDetailId(null);
            setResultsId(id);
          }}
        />

        {resultsId ? (
          <TaskResultsModal
            open={!!resultsId}
            onClose={() => setResultsId(null)}
            accessToken={accessToken}
            taskId={resultsId}
          />
        ) : null}
      </div>
    </BrandThemeProvider>
  );
}

export default TaskListView;
